import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useInAppWindowFocus } from "@/lib/inAppWindowFocus";
import { MODAL_STACK_GROUP_PROJECT_OVERVIEW, useModalStackControls } from "@/lib/modalStack";
import {
  ProjectOverviewContext,
  type ProjectOverviewOpenOptions,
  type ProjectOverviewReplyDraft,
} from "@/projectOverview/projectOverviewContextValue";
import { ProjectOverviewDialog } from "@/projectOverview/ProjectOverviewDialog";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import { useProjectOpenHandoff } from "@/projectOverview/useProjectOpenHandoff";
import { useProjectOpenRealtime } from "@/projectOverview/useProjectOpenRealtime";
import { useProjectOverviewDeepLink } from "@/projectOverview/useProjectOverviewDeepLink";

export function ProjectOverviewProvider({ children }: { children: ReactNode }) {
  const { focusSurface } = useInAppWindowFocus();
  const { bringGroupToFront } = useModalStackControls();
  const [isOpen, setIsOpen] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [highlightNoteId, setHighlightNoteId] = useState<number | null>(null);
  const [replyDraft, setReplyDraft] = useState<ProjectOverviewReplyDraft | null>(null);
  const [openMontageunterlagen, setOpenMontageunterlagen] = useState(false);
  const [stuecklisteId, setStuecklisteId] = useState<number | null>(null);

  const open = useCallback((auftragId: number, options?: ProjectOverviewOpenOptions) => {
    const id = Number(auftragId);
    if (!Number.isFinite(id) || id <= 0) return;
    const noteId = options?.highlightNoteId;
    setHighlightNoteId(
      noteId != null && Number.isFinite(Number(noteId)) && Number(noteId) > 0
        ? Number(noteId)
        : null,
    );
    const draft = options?.replyDraft;
    setReplyDraft(draft?.key ? draft : null);
    const slId = options?.stuecklisteId;
    setStuecklisteId(
      slId != null && Number.isFinite(Number(slId)) && Number(slId) > 0 ? Number(slId) : null,
    );
    setOpenMontageunterlagen(Boolean(options?.openMontageunterlagen || slId));
    setActiveId(id);
    focusSurface("projectOverview");
    bringGroupToFront(MODAL_STACK_GROUP_PROJECT_OVERVIEW);
    setIsOpen(true);
  }, [focusSurface, bringGroupToFront]);

  const close = useCallback(() => {
    setIsOpen(false);
    setActiveId(null);
    setHighlightNoteId(null);
    setReplyDraft(null);
    setOpenMontageunterlagen(false);
    setStuecklisteId(null);
  }, []);

  const value = useMemo(
    () => ({
      open,
      close,
      isOpen,
      activeId,
      highlightNoteId,
      replyDraft,
      openMontageunterlagen,
      stuecklisteId,
    }),
    [open, close, isOpen, activeId, highlightNoteId, replyDraft, openMontageunterlagen, stuecklisteId],
  );

  return (
    <ProjectOverviewContext.Provider value={value}>
      {children}
      <ProjectOverviewDeepLinkBridge />
    </ProjectOverviewContext.Provider>
  );
}

/** Dialog innerhalb von TimesheetFlowProvider mounten (nutzt useTimesheetFlow). */
export function ProjectOverviewDialogMount() {
  // Deep-Link hier: braucht TimesheetFlow, um den Stundennachweis zu schließen.
  useProjectOverviewDeepLink();
  const {
    isOpen,
    activeId,
    highlightNoteId,
    replyDraft,
    openMontageunterlagen,
    stuecklisteId,
    close,
  } = useProjectOverview();
  return (
    <ProjectOverviewDialog
      open={isOpen}
      orderId={activeId}
      highlightNoteId={highlightNoteId}
      replyDraft={replyDraft}
      openMontageunterlagen={openMontageunterlagen}
      stuecklisteId={stuecklisteId}
      onClose={close}
    />
  );
}

function ProjectOverviewDeepLinkBridge() {
  useProjectOpenHandoff();
  useProjectOpenRealtime();
  return null;
}
